import * as React from 'react';
import useUpdateRef from '../../../../hooks/useUpdateRef';
import { useDebounceCallback } from '../../../../hooks/code/useDebounce';
import Dropdown from '../../Dropdown/Dropdown';
import Input from '../../Input/Input';
import { AddFilterDropdown } from '../AddFilter/AddFilter';
import type { FilterItem, NumberFilterItem } from '../../../../types/Filter';
import type { OnFilterChangeHandler } from '../types';
import DeleteFilterButton from './DeleteFilterButton';
import Styles from './filter-views.module.scss';

type Props = {
    filterItem: NumberFilterItem;
    availableFilters: FilterItem[];
    onChange: OnFilterChangeHandler;
};

const DROPDOWN_ITEMS = [
    { name: 'Equals', value: 'eq' },
    { name: 'Does not equal', value: 'neq' },
    { name: 'Greater or equals', value: 'gte' },
    { name: 'Lower or equals', value: 'lte' }
];

const NumberFilterView = (props: Props) => {
    const { filterItem } = props;
    const onChangeRef = useUpdateRef(props.onChange);

    const [value, setValue] = React.useState(filterItem.value?.toString() ?? '');
    React.useEffect(() => {
        setValue(filterItem.value?.toString() ?? '');
    }, [filterItem.value]);

    const changeValue = useDebounceCallback(
        () => {
            const intValue = parseInt(value, 10);
            if (!Number.isNaN(intValue) && intValue !== filterItem.value) {
                onChangeRef.current?.({
                    ...filterItem,
                    value: intValue
                });
            }
        },
        { delay: 500, changes: [value, filterItem] }
    );

    const selectedOperatorItem = DROPDOWN_ITEMS.find((item) => item.value === filterItem.operator);

    return (
        <div className={Styles['filter-view']}>
            <AddFilterDropdown
                availableFilters={props.availableFilters}
                value={filterItem}
                onAdd={(filterItem) => filterItem !== props.filterItem && props.onChange(filterItem)}
            />
            <Dropdown
                options={DROPDOWN_ITEMS}
                value={filterItem.operator}
                menuClassName={Styles['filter-view-dropdown-tooltip']}
                className={Styles.control}
                onChange={(operator) => {
                    changeValue.cancel();
                    if (operator !== filterItem.operator) {
                        props.onChange({
                            ...filterItem,
                            operator: operator as NumberFilterItem['operator']
                        });
                    }
                }}
                dataTip={selectedOperatorItem?.name}
                dataTipOverflow
            />
            <Input
                type="number"
                placeholder="Enter number"
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    changeValue();
                }}
                onBlur={() => {
                    changeValue.flush();
                }}
            />
            <DeleteFilterButton {...props} />
        </div>
    );
};

export default NumberFilterView;
